import {AbstractQITMeetingState, AbstractState} from "./DHTStates";

export class AfterInterviewsState extends AbstractState {
  getTitle() {
    return super.getTitle() + ": Interviews";
  }
  getStakeholderState (stakeholder) {
    if (stakeholder.id === "managerC") {
      return super.getStakeholderState({
        data: `<p class="my-3 lg:my-0 text-brand-blue-600 text-xl lg:text-2xl italic leading-snug lg:w-5/6">
          “The vaccinators send me their data, and I send it to the district level so that they can analyze it. That’s my job, and that’s all I really have time for.”
        </p>
        <p class="my-3">
          The facility manager recently started this job and feels overwhelmed by the amount of work.<br/><br/>
          He is not opposed to the change, but he is worried about the perceived extra workload in implementing it.
        </p>`
      });
    }
    if (stakeholder.id === "vaccinatorC") {
      return super.getStakeholderState({
        data: `<p class="my-3 lg:my-0 text-brand-blue-600 text-xl lg:text-2xl italic leading-snug lg:w-5/6">
          “Changes like this come and go. It is easier to just keep doing our jobs as usual.”
        </p>
        <p class="my-3">
          The vaccinators at this facility say they don’t really have an opinion on this change.
        </p>`
      });
    }
    if (stakeholder.id === "communityC") {
      return super.getStakeholderState({
        data: `<p class="my-3">
          Densely populated informal urban settlements make it harder to reach and track the children served by this facility.
        </p>`
      });
    }
  }
}

export class FromDataAnalysisState extends AbstractState {
  getTitle() {
    return super.getTitle() + ": Data Analysis";
  }
  getStakeholderState (stakeholder) {
    if (stakeholder.id === "managerC") {
      return super.getStakeholderState({
        data: `<p class="my-3 lg:my-0 text-brand-blue-600 text-xl lg:text-2xl italic leading-snug lg:w-5/6">
          “Those numbers are worse than I thought. I still do not think that I have time to solve this problem.”
        </p>`
      })
    }
    if (stakeholder.id === "communityC") {
      return super.getStakeholderState({
        data: `<p class="my-3 lg:my-0">
          You meet with the manager at this facility to review its data together.<br/><br/>
          Overall, this facility has <span class="border-b-2 border-dotted border-gray-800" data-tippy-content="For more information about analyzing facility data based on access and utilization, see the Resources section.">poor access and relatively poor ongoing utilization of routine immunization services</span>.
        </p>`
      })
    }
  }
}

export class GoAndSeeState extends AbstractState {
  getStakeholderState (stakeholder) {}
}

export class QITMeetingState extends AbstractQITMeetingState {
  getStakeholderState (stakeholder) {
    if (stakeholder.id === "communityC") {
      return super.getStakeholderState({
        data: this.getGenericQITNotes()
      })
    }
  }
}

export class FirstSuccessfulQITMeetingState extends AbstractQITMeetingState {
  getStakeholderState (stakeholder) {
    if (stakeholder.id === "managerC") {
      return super.getStakeholderState({
        data: `<p class="my-3 lg:my-0 text-brand-blue-600 text-xl lg:text-2xl italic leading-snug lg:w-5/6">
          “Perhaps if the community can help, this will not result in as much extra work as I worried.”
        </p>`
      })
    }
    if (stakeholder.id === "communityC") {
      return super.getStakeholderState({
        data: this.getGenericQITNotes() + `
          <p class="my-5 font-bold">
            The facility sends the following additional notes about the meeting:
          </p>
          <ul class="my-5 lg:my-0 list-disc list-inside">
            <li>
              Based on discussions with the community, we decided to focus on the challenge of tracking the births of children born in the informal urban settlement that this facility serves. Many of these children are not being vaccinated and, even if they are initially, they are not being brought for follow up.
            </li>
            <li>
              At this meeting, a community leader agreed to talk with traditional birth attendants, to see if they can assist.
            </li>
            <li>
              Birth attendants agreed to help track births and encourage vaccination. Families tracked in this way will also be able to receive SMS reminders to bring their children for vaccination.
            </li>
          </ul>`
      })
    }
  }
}

export class FollowupQITMeetingState extends AbstractQITMeetingState {
  getStakeholderState (stakeholder) {
    if (stakeholder.id === "communityC") {
      return super.getStakeholderState({
        data: this.getGenericQITNotes() + `
          <p class="my-5 font-bold">
            The facility sends the following additional notes about the meeting:
          </p>
          <ul class="my-5 lg:my-0 list-disc list-inside">
            <li>
              The tracking via birth attendants is going well, with a 29% increase in children from this community being brought for vaccination.
            </li>
            <li>
              The SMS messages were working well for some families. Based on the results of the first <span class="whitespace-no-wrap border-b-2 border-dotted border-gray-800" data-tippy-content="PDSA stands for Plan Do Study Act. For more information about this and other problem solving tools, see the Resources section.">PDSA cycle</span>, we have expanded the service to include calls in cases where the cell phone owner is illiterate.
            </li>
          </ul>`
      })
    }
  }
}

export class MicroPlanningMeetingState extends AbstractQITMeetingState {
  getTitle() {
    return "Week " + this.getWeek() + ": Micro Planning";
  }
  getStakeholderState (stakeholder) {
    if (stakeholder.id === "communityC") {
      return super.getStakeholderState({
        data: this.getMicroPlanningNotes()
      })
    }
  }
}

export class WhenReaches80PercentAdoptionState extends AbstractState {
  getTitle() {
    return super.getTitle() + ": Adoption";
  }
  getStakeholderState (stakeholder) {
    if (stakeholder.id === "managerC") {
      return super.getStakeholderState({
        data: `<p class="my-3 lg:my-0 text-brand-blue-600 text-xl lg:text-2xl italic leading-snug lg:w-5/6">
          “The quality improvement teams are not as time consuming as I thought. Also, I really appreciate that I can now analyze my own data and use that to plan more efficient, comprehensive services.”
        </p>`
      })
    }
    if (stakeholder.id === "vaccinatorC") {
      return super.getStakeholderState({
        data: `<p class="my-3 lg:my-0 text-brand-blue-600 text-xl lg:text-2xl italic leading-snug lg:w-5/6">
          “Community involvement has helped us reach pockets of children that we were not even aware of in the community.”
        </p>`
      })
    }
    if (stakeholder.id === "communityC") {
      return super.getStakeholderState({
        data: "This facility is now regularly using quality improvement teams for problem solving and more effective micro planning, with the active input of the community."
      })
    }
  }
}
